import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Package, XCircle, MessageCircle } from "lucide-react";
import { api, inr, setSeo } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSite } from "@/context/SiteContext";

export default function OrderConfirmation() {
  const { oid } = useParams();
  const { clearCart } = useCart();
  const { settings } = useSite();
  useEffect(() => setSeo("Order Status | Adam's The Islamic Boutique", "Your order status."), []);
  const { data: order } = useQuery({ queryKey: ["order", oid], queryFn: async () => (await api.get(`/orders/${oid}`)).data });

  const paid = order?.payment_status === "paid";
  const failed = order?.payment_status === "failed";

  useEffect(() => {
    if (paid) { clearCart(); try { localStorage.removeItem("adams_pending_order"); } catch { /* storage unavailable (private mode) — non-critical */ } }
  }, [paid, clearCart]);

  const waNumber = (settings?.whatsapp || "").replace(/\D/g, "");
  const waMessage = order
    ? [
        "*Order Confirmation — Adam's The Islamic Boutique*",
        "",
        `Order ID: ${order.id?.slice(0, 8).toUpperCase()}`,
        `Order Total: ${inr(order.total)}`,
        "",
        "Your order will be dispatched within 24–48 hours.",
        "",
        "Thank you for shopping at Adam's! 🤍",
      ].join("\n")
    : "";
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}` : "";

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 lg:py-24 text-center">
      <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${failed ? "bg-[#a13b2f]/10" : "bg-champagne/15"}`}>
        {failed ? <XCircle className="w-11 h-11 text-[#a13b2f]" /> : <CheckCircle2 className="w-11 h-11 text-champagne" />}
      </div>
      <p className="overline mb-3">{failed ? "Payment Failed" : paid ? "Order Confirmed" : "Order Received"}</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-espresso" data-testid="order-confirmed-title">
        {failed ? "Payment Was Not Completed" : "Thank You for Your Order"}
      </h1>
      <p className="text-espresso/60 mt-4">
        {failed
          ? "Your payment could not be completed. No amount has been captured. You can try again from your bag."
          : paid
          ? "Your order has been placed successfully. A confirmation has been sent to your email."
          : "We've received your order and are awaiting payment confirmation."}
      </p>

      {order && (
        <div className="bg-beige/50 rounded-3xl p-7 mt-10 text-left">
          <div className="flex items-center justify-between mb-5">
            <span className="text-xs text-espresso/50">Order ID</span>
            <span className="font-mono text-sm text-espresso">{order.id?.slice(0, 8).toUpperCase()}</span>
          </div>
          <div className="space-y-4 border-t border-champagne/25 pt-5">
            {order.items?.map((it, i) => (
              <div key={i} className="flex gap-3 items-center">
                <img src={it.image} alt={it.name} className="w-12 h-14 object-cover rounded-lg" />
                <div className="flex-1 text-sm"><p className="font-serif text-espresso">{it.name}</p><p className="text-xs text-espresso/50">Qty {it.quantity}</p></div>
                <span className="text-sm">{inr(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-champagne/25 mt-5 pt-4 flex justify-between font-serif text-xl text-espresso">
            <span>{paid ? "Total Paid" : "Order Total"}</span><span>{inr(order.total)}</span>
          </div>
        </div>
      )}

      {!failed && order && waLink && (
        <div className="mt-8 bg-[#25D366]/10 border border-[#25D366]/30 rounded-3xl p-6" data-testid="wa-confirm-box">
          <p className="text-sm text-espresso/70 mb-4">Get your order confirmation on WhatsApp — tap below and send us the pre-filled message.</p>
          <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-3.5 rounded-full text-sm font-medium hover:bg-[#1ebe5b] transition-colors" data-testid="wa-confirm-btn">
            <MessageCircle className="w-4 h-4" /> Confirm on WhatsApp
          </a>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
        {failed ? (
          <Link to="/cart" className="bg-espresso text-ivory px-8 py-3.5 rounded-full text-sm">Return to Bag &amp; Retry</Link>
        ) : (
          <Link to="/shop" className="bg-espresso text-ivory px-8 py-3.5 rounded-full text-sm">Continue Shopping</Link>
        )}
        <Link to="/" className="border border-espresso/20 px-8 py-3.5 rounded-full text-sm flex items-center justify-center gap-2"><Package className="w-4 h-4" /> Back to Home</Link>
      </div>
    </div>
  );
}
